import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import {
  ArrowLeft, MapPin, ArrowRight, Package, Truck,
  User, CalendarDays, Container, FileText, Hash,
  Printer, Shield, Banknote,
  History,
} from "lucide-react";
import { printSingleLR } from "../../utils/printLR";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

import ConsignmentMovement from "@/components/ConsignmentMovement";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLS = {
  Active:       "bg-blue-50 text-blue-700 border-blue-200",
  "Pick-Up Done": "bg-blue-50 text-blue-700 border-blue-200",
  Scheduled:    "bg-violet-50 text-violet-700 border-violet-200",
  "In Transit": "bg-blue-50 text-blue-700 border-blue-200",
  "At Warehouse": "bg-purple-50 text-purple-700 border-purple-200",
  "Out for Delivery": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Awaiting Completion": "bg-orange-50 text-orange-700 border-orange-200",
  "Delivery Complete": "bg-green-50 text-green-700 border-green-200",
  "On Hold": "bg-red-50 text-red-700 border-red-200",
  Completed:    "bg-green-50 text-green-700 border-green-200",
};

const TYPE_BADGE = {
  from_location: "bg-slate-100 text-slate-600 border-slate-200",
  warehouse:     "bg-purple-100 text-purple-700 border-purple-200",
  to_location:   "bg-teal-100 text-teal-700 border-teal-200",
};

const TYPE_LABEL = {
  from_location: "Pickup",
  warehouse:     "Warehouse",
  to_location:   "Delivery",
};

const visibleStatus = status => ({
  "Awaiting Pickup": "Pick-Up Done",
  "Awaiting Completion": "Out for Delivery",
  "Delivery Complete": "Completed",
  "Complete": "Completed",
  "Delivered": "Completed",
}[status] || status);

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#F1F5F9] last:border-b-0">
      <div className="w-7 h-7 bg-[#F1F5F9] rounded-sm flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={13} className="text-[#1B2A6B]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">{label}</p>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}

export default function TransferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState(null);
  const [lrVersions, setLRVersions] = useState([]);
  const [generatingLR, setGeneratingLR] = useState(false);
  const [loading, setLoading]   = useState(true);
  const qrRefs = useRef([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/consignments/${id}`, { withCredentials: true }),
      axios.get(`${API}/consignments/${id}/lr-versions`, { withCredentials: true }),
    ])
      .then(([transferResponse, versionsResponse]) => {
        setTransfer(transferResponse.data);
        setLRVersions(versionsResponse.data);
      })
      .catch(() => { toast.error("Transfer not found."); navigate("/consignments"); })
      .finally(() => setLoading(false));
  }, [id]);

  const generateLR = async () => {
    const printWindow = window.open("", "_blank", "width=900,height=700,toolbar=no,menubar=no");
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups to generate the LR.");
      return;
    }
    printWindow.document.write("<p style='font-family:Arial;padding:24px'>Generating latest LR...</p>");
    setGeneratingLR(true);
    try {
      const { data } = await axios.post(`${API}/consignments/${id}/lr-versions`, {}, { withCredentials: true });
      setLRVersions(previous => previous.some(version => version.id === data.id) ? previous : [...previous, data]);
      printSingleLR(data.snapshot, printWindow);
    } catch (error) {
      printWindow.close();
      toast.error(error.response?.data?.detail || "Failed to generate LR.");
    } finally {
      setGeneratingLR(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[#94A3B8]">
        Loading transfer...
      </div>
    );
  }

  if (!transfer) return null;

  const loads = transfer.load_details || [];
  const movements = transfer.movement_history || [];

  const printAllQR = () => {
    const cards = qrRefs.current.map((ref, i) => {
      if (!ref) return null;
      const svg = ref.querySelector("svg");
      if (!svg) return null;
      const clone = svg.cloneNode(true);
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const svgStr = new XMLSerializer().serializeToString(clone);
      const imgSrc = `data:image/svg+xml,${encodeURIComponent(svgStr)}`;
      return { imgSrc, load: loads[i] };
    }).filter(Boolean);

    if (cards.length === 0) {
      toast.error("No loads to print.");
      return;
    }

    const win = window.open("", "_blank", "width=820,height=600,toolbar=no,menubar=no,scrollbars=yes");
    if (!win) { toast.error("Pop-up blocked. Allow pop-ups for this site."); return; }

    const cardsHtml = cards.map(({ imgSrc, load }) => `
      <div class="card">
        <img src="${imgSrc}" alt="QR ${load.load_ref}" />
        <p class="ref">${load.load_ref}</p>
        ${load.load_name ? `<p class="name">${load.load_name}</p>` : ""}
        ${load.handle_with_care ? `<div class="hwc">&#9888; Handle with Care</div>` : ""}
      </div>`).join("");

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>QR Codes — ${transfer.lr_number || "All Loads"}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Courier New', monospace; padding: 12px; background: #fff; }
    h2 { font-size: 13px; color: #1B2A6B; letter-spacing: 2px; margin-bottom: 12px; font-family: sans-serif; }
    .grid { display: flex; flex-wrap: wrap; gap: 12px; }
    .card { text-align: center; border: 2px solid #1B2A6B; border-bottom: 5px solid #FFD700; padding: 10px 12px; }
    img { width: 120px; height: 120px; display: block; margin: 0 auto; }
    .ref { font-size: 13px; font-weight: 900; letter-spacing: 3px; color: #1B2A6B; margin-top: 8px; }
    .name { font-size: 9px; color: #475569; margin-top: 3px; font-family: sans-serif; }
    .hwc { margin-top: 5px; padding: 3px 7px; background: #FFF7ED; border: 2px solid #F59E0B; color: #92400E; font-size: 9px; font-weight: 900; letter-spacing: 1px; text-transform: uppercase; font-family: sans-serif; }
    @media print { @page { size: A4; margin: 10mm; } .card { break-inside: avoid; } }
  </style>
</head>
<body>
  <h2>LR: ${transfer.lr_number || "—"} &nbsp;|&nbsp; ${cards.length} Load${cards.length !== 1 ? "s" : ""}</h2>
  <div class="grid">${cardsHtml}</div>
  <script>window.onload = function(){ window.print(); };<\/script>
</body>
</html>`);
    win.document.close();
  };

  return (
    <div className="space-y-5">
      {/* Back */}
      <button
        onClick={() => navigate("/consignments")}
        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#475569] hover:text-[#1B2A6B] transition-colors"
      >
        <ArrowLeft size={14} /> Back to Consignments
      </button>

      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono font-black text-2xl text-[#1B2A6B] bg-[#EEF0FA] px-3 py-1 border border-[#C7D2FE] tracking-widest">
              {transfer.lr_number || "—"}
            </span>
            <span className={`inline-block px-3 py-1 text-[11px] font-bold uppercase tracking-widest border rounded-sm ${STATUS_CLS[visibleStatus(transfer.status)] || STATUS_CLS.Scheduled}`}>
              {visibleStatus(transfer.status)}
            </span>
          </div>
          <p className="text-sm text-[#475569] mt-2">{transfer.customer_name}</p>
        </div>
        <button
          onClick={generateLR}
          disabled={generatingLR}
          data-testid="generate-lr-btn"
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1B2A6B] hover:text-white border-2 border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-4 py-2.5"
        >
          <Printer size={14} />{generatingLR ? "Generating..." : "Generate LR"}
        </button>
      </div>

      <ConsignmentMovement transfer={transfer} onSaved={setTransfer} />

      <div className="bg-white border border-[#E2E8F0]" data-testid="lr-history">
        <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History size={15} className="text-[#1B2A6B]" />
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#1B2A6B]">LR History</h3>
          </div>
          <span className="text-xs text-[#94A3B8]">{lrVersions.length} version{lrVersions.length !== 1 ? "s" : ""}</span>
        </div>
        {lrVersions.length === 0 ? (
          <p className="px-4 py-6 text-sm text-[#94A3B8]">No LR version generated yet.</p>
        ) : (
          <div className="divide-y divide-[#F1F5F9]">
            {lrVersions.map(version => (
              <div key={version.id} className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-black text-[#1B2A6B]">Version {version.version}</span>
                    {version.trip_number && <span className="font-mono text-[10px] text-[#64748B]">{version.trip_number}</span>}
                  </div>
                  <p className="text-sm text-[#0F172A] mt-0.5">{version.origin_label || "—"} → {version.destination_label || "—"}</p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">
                    Generated {format(new Date(version.generated_at), "dd MMM yyyy, hh:mm a")} by {version.generated_by || "System"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => printSingleLR(version.snapshot)}
                  className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#1B2A6B] border border-[#1B2A6B] hover:bg-[#1B2A6B] hover:text-white px-3 py-1.5 transition-colors"
                >
                  <Printer size={11} />Print
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Left: Core details */}
        <div className="lg:col-span-2 space-y-5">

          {/* Route card */}
          <div className="bg-white border border-[#E2E8F0]">
            <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0]">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#1B2A6B]">Route</h3>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-4 flex-wrap">
                {/* Origin */}
                <div className="flex flex-col items-start gap-1">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border ${TYPE_BADGE[transfer.origin_type] || TYPE_BADGE.from_location}`}>
                    {TYPE_LABEL[transfer.origin_type] || transfer.origin_type}
                  </span>
                  <p className="font-semibold text-[#0F172A] text-sm">{transfer.pickup_zone || "—"}</p>
                </div>

                <ArrowRight size={20} className="text-[#CBD5E1] shrink-0" />

                {/* Destination */}
                <div className="flex flex-col items-start gap-1">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border ${TYPE_BADGE[transfer.destination_type] || TYPE_BADGE.to_location}`}>
                    {TYPE_LABEL[transfer.destination_type] || transfer.destination_type}
                  </span>
                  <p className="font-semibold text-[#0F172A] text-sm">{transfer.drop_zone || transfer.drop_area || "—"}</p>
                </div>
              </div>
            </div>
          </div>

          {transfer.location_events?.length > 0 && <div className="bg-white border p-4">
            <h3 className="font-semibold mb-2">Location Activity</h3>
            {transfer.location_events.map((event, i) => <p key={i} className="text-sm py-2 border-b">{event.action} · {event.location} · {event.by} · {new Date(event.at).toLocaleString()}</p>)}
          </div>}
          {/* Consignor / Consignee card */}
          {(transfer.consignor_name || transfer.consignee_name) && (
            <div className="bg-white border border-[#E2E8F0]">
              <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0]">
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#1B2A6B]">Consignor & Consignee</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#F1F5F9]">
                <div className="p-4 space-y-0.5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] mb-2">Consignor (Sender)</p>
                  <p className="font-semibold text-sm text-[#0F172A]">{transfer.consignor_name || "—"}</p>
                  {transfer.consignor_phone && <p className="text-xs text-[#475569]">{transfer.consignor_phone}</p>}
                  {transfer.pickup_address && <p className="text-xs text-[#64748B] mt-1">{transfer.pickup_address}</p>}
                </div>
                <div className="p-4 space-y-0.5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] mb-2">Consignee (Receiver)</p>
                  <p className="font-semibold text-sm text-[#0F172A]">{transfer.consignee_name || "—"}</p>
                  {transfer.consignee_phone && <p className="text-xs text-[#475569]">{transfer.consignee_phone}</p>}
                  {transfer.drop_address && <p className="text-xs text-[#64748B] mt-1">{transfer.drop_address}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Loads card */}
          <div className="bg-white border border-[#E2E8F0]">
            <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0]">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#1B2A6B]">Movement History</h3>
            </div>
            {movements.length === 0 ? (
              <p className="px-4 py-6 text-sm text-[#94A3B8]">No trip-sheet movements recorded yet.</p>
            ) : (
              <div className="divide-y divide-[#F1F5F9]">
                {movements.map((movement, index) => (
                  <div key={`${movement.trip_id}-${index}`} className="px-4 py-4 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#EEF0FA] text-[#1B2A6B] flex items-center justify-center text-xs font-bold shrink-0">{index + 1}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold text-[#1B2A6B]">{movement.trip_number || "Trip Sheet"}</span>
                        <span className="text-[10px] uppercase font-bold text-[#64748B]">{movement.status || "Open"}</span>
                      </div>
                      <p className="text-sm text-[#0F172A] mt-1">{movement.origin_label || "—"} → {movement.destination_label || "—"}</p>
                      <p className="text-xs text-slate-500">{movement.event_version !== 1 ? "Legacy assignment — arrival not confirmed" : [movement.departed_at && `Departed: ${new Date(movement.departed_at).toLocaleString()}`, movement.arrived_at && `Arrived: ${new Date(movement.arrived_at).toLocaleString()}`].filter(Boolean).join(" · ")}</p>
                      {movement.vehicle_display && <p className="text-xs text-[#94A3B8] mt-0.5">{movement.vehicle_display}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Loads card */}
          <div className="bg-white border border-[#E2E8F0]">
            <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0] flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#1B2A6B]">Loads</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#94A3B8] font-semibold">{loads.length} item{loads.length !== 1 ? "s" : ""}</span>
                {loads.length > 0 && (
                  <button
                    onClick={printAllQR}
                    data-testid="print-all-qr-btn"
                    className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1B2A6B] bg-[#EEF0FA] hover:bg-[#1B2A6B] hover:text-white border border-[#C7D2FE] hover:border-[#1B2A6B] transition-colors px-2.5 py-1 rounded-sm"
                  >
                    <Printer size={11} />Print All QR
                  </button>
                )}
              </div>
            </div>
            {/* Hidden QR renders for print */}
            <div className="hidden" aria-hidden="true">
              {loads.map((l, i) => (
                <div key={i} ref={el => { qrRefs.current[i] = el; }}>
                  <QRCodeSVG value={l.load_ref || `load-${i + 1}`} size={150} bgColor="#FFFFFF" fgColor="#1B2A6B" level="H" />
                </div>
              ))}
            </div>
            {loads.length === 0 ? (
              <div className="flex items-center justify-center py-10">
                <p className="text-sm text-[#94A3B8]">No load details recorded.</p>
              </div>
            ) : (
              <div className="divide-y divide-[#F1F5F9]">
                {loads.map((l, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <Package size={14} className="text-[#94A3B8] shrink-0" />
                    <span className="font-mono text-xs font-bold text-[#1B2A6B] bg-[#EEF0FA] px-2 py-0.5 border border-[#C7D2FE] shrink-0">
                      {l.load_ref || `#${i + 1}`}
                    </span>
                    <span className="text-sm text-[#0F172A] font-medium">{l.load_name || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Meta info */}
        <div className="bg-white border border-[#E2E8F0] h-fit">
          <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0]">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#1B2A6B]">Details</h3>
          </div>
          <div className="px-4">
            <DetailRow icon={Hash} label="LR Number">
              <span className="font-mono font-black text-[#1B2A6B]">{transfer.lr_number || "—"}</span>
            </DetailRow>

            <DetailRow icon={CalendarDays} label="Transfer Date">
              <span className="text-sm text-[#0F172A]">
                {transfer.transfer_date
                  ? format(new Date(transfer.transfer_date + "T00:00:00"), "dd MMM yyyy")
                  : "—"}
              </span>
            </DetailRow>

            <DetailRow icon={User} label="Customer">
              <span className="text-sm text-[#0F172A] font-medium">{transfer.customer_name || "—"}</span>
            </DetailRow>

            <DetailRow icon={Container} label="Consignment">
              {transfer.consignment_number ? (
                <span className="font-mono font-bold text-sm text-[#1B2A6B]">{transfer.consignment_number}</span>
              ) : (
                <span className="text-sm text-[#94A3B8] italic">Not assigned</span>
              )}
            </DetailRow>

            <DetailRow icon={Truck} label="Vehicle">
              <span className="text-sm text-[#0F172A]">{transfer.vehicle_display || "—"}</span>
            </DetailRow>

            <DetailRow icon={MapPin} label="Current Location">
              <span className="text-sm text-[#0F172A] font-medium">{transfer.current_location || "—"}</span>
            </DetailRow>

            <DetailRow icon={Package} label="Quantity">
              <span className="text-sm text-[#0F172A] font-medium">{transfer.quantity || loads.length || "—"}</span>
            </DetailRow>

            {transfer.booking_id && (
              <DetailRow icon={FileText} label="Booking">
                <Link
                  to={`/bookings/${transfer.booking_id}`}
                  className="text-sm text-[#1B2A6B] font-semibold hover:underline flex items-center gap-1"
                >
                  View Booking <ArrowRight size={11} />
                </Link>
              </DetailRow>
            )}

            {(transfer.freight_charges || transfer.to_pay_amount) && (
              <DetailRow icon={Banknote} label="Freight / To Pay">
                <span className="text-sm text-[#0F172A] font-semibold">
                  {transfer.freight_charges ? `₹${transfer.freight_charges}` : "—"}
                </span>
                {transfer.to_pay_amount && (
                  <span className="text-xs text-[#94A3B8] ml-2">Balance: ₹{transfer.to_pay_amount}</span>
                )}
              </DetailRow>
            )}

            {transfer.eway_bill_no && transfer.eway_bill_no !== "NA" && (
              <DetailRow icon={Hash} label="E-Way Bill">
                <span className="text-sm font-mono text-[#0F172A]">{transfer.eway_bill_no}</span>
              </DetailRow>
            )}

            {transfer.insurance_opted && (
              <DetailRow icon={Shield} label="Insurance">
                <span className={`text-sm font-bold ${transfer.insurance_opted === "YES" ? "text-green-700" : "text-[#94A3B8]"}`}>
                  {transfer.insurance_opted}
                </span>
                {transfer.risk_coverage_type && (
                  <span className="text-xs text-[#475569] ml-2">· {transfer.risk_coverage_type}</span>
                )}
              </DetailRow>
            )}

            {transfer.booking_incharge && (
              <DetailRow icon={User} label="Booking Incharge">
                <span className="text-sm text-[#0F172A]">{transfer.booking_incharge}</span>
              </DetailRow>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
